// A root's own contentData may predate the backend attaching chatJobData to
// every message in a thread (not just its root) — in that case fall back to
// any of its replies' chatJobData, so the thread doesn't lose its job card
// (and, since replies only ever render inside a job group's inline preview,
// become invisible) just because its opening message is older than that fix.
// Takes a parentId -> replies map (built once per call, not per message) so
// resolving a whole conversation stays O(n) instead of re-scanning every
// message for each root.
const resolveChatJobData = (message, repliesByParent) => {
  const ownJobData = message?.contentData?.metaData?.chatJobData;
  if (ownJobData) return ownJobData;
  const replies = repliesByParent.get(message.messageId) || [];
  for (const reply of replies) {
    const replyJobData = reply?.contentData?.metaData?.chatJobData;
    if (replyJobData) return replyJobData;
  }
  return null;
};

export const groupMessagesByJob = (messages) => {
  // Filter to only include main messages (not thread replies)
  const mainMessages = messages.filter((msg) => !msg.parentMessageId);

  const repliesByParent = new Map();
  for (const msg of messages) {
    if (!msg.parentMessageId) continue;
    if (!repliesByParent.has(msg.parentMessageId)) {
      repliesByParent.set(msg.parentMessageId, []);
    }
    repliesByParent.get(msg.parentMessageId).push(msg);
  }

  const jobDataByMessageId = new Map(
    mainMessages.map((msg) => [
      msg.messageId,
      resolveChatJobData(msg, repliesByParent),
    ]),
  );

  const hasChatJobData = [...jobDataByMessageId.values()].some(Boolean);

  if (!hasChatJobData) {
    return { grouped: false, items: mainMessages };
  }

  const groups = [];
  let currentJobId = null;
  let currentGroup = null;

  mainMessages.forEach((message) => {
    const chatJobData = jobDataByMessageId.get(message.messageId);
    const jobId = chatJobData?.jobId;

    if (jobId && jobId !== currentJobId) {
      // New job — start a new job group
      currentJobId = jobId;
      currentGroup = {
        jobId,
        profession: chatJobData?.profession,
        healthcareWorkplaceId: chatJobData?.healthcareWorkplaceId,
        healthcareWorkplaceName: chatJobData?.healthcareWorkplaceName,
        referenceNumber: chatJobData?.referenceNumber,
        timestamp: message.createdAt,
        messages: [message],
      };
      groups.push(currentGroup);
    }
    else if (jobId && jobId === currentJobId && currentGroup) {
      // Same job — append to current group
      currentGroup.messages.push(message);
    }
    else if (!jobId) {
      // No job context — always a standalone group (never merge into a job group)
      currentJobId = null;
      currentGroup = {
        jobId: null,
        profession: null,
        healthcareWorkplaceName: null,
        timestamp: message.createdAt,
        messages: [message],
      };
      groups.push(currentGroup);
    }
  });

  return { grouped: true, items: groups };
};

export const getThreadMessages = (messages, parentMessageId) => {
  return messages.filter((msg) => msg.parentMessageId === parentMessageId);
};

// Returns up to `limit` most recent replies for each parent message
export const getRecentRepliesByParent = (messages, limit = 3) => {
  const repliesByParent = {};
  for (const msg of messages) {
    if (!msg.parentMessageId) continue;
    if (!repliesByParent[msg.parentMessageId]) {
      repliesByParent[msg.parentMessageId] = [];
    }
    repliesByParent[msg.parentMessageId].push(msg);
  }
  const result = {};
  for (const [parentId, replies] of Object.entries(repliesByParent)) {
    replies.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    result[parentId] = replies.slice(-limit);
  }
  return result;
};

// Returns the unread count for a specific job from a user's chat profile
export const getUnreadCountForJob = (profile, jobId) => {
  if (!profile) return 0;
  const unreadByJob = profile.unreadByJob;
  const hasUnreadByJob = unreadByJob && Object.keys(unreadByJob).length > 0;
  return hasUnreadByJob
    ? (unreadByJob[jobId] ?? 0)
    : (profile.lastReadMessageCount || 0);
};

// Returns total reply count per parent message
export const getReplyCountByParent = (messages) => {
  const counts = {};
  for (const msg of messages) {
    if (!msg.parentMessageId) continue;
    counts[msg.parentMessageId] = (counts[msg.parentMessageId] || 0) + 1;
  }
  return counts;
};
